/**
 * useViewportBreakpoint Hook (Story 5.4, Task 7)
 *
 * Returns the current viewport breakpoint:
 * - "mobile": < 768px
 * - "tablet": 768-1024px
 * - "desktop": > 1024px
 *
 * Listens to window resize events with 150ms debounce to avoid
 * excessive re-renders during drag resize operations.
 */

import { useState, useEffect, useRef } from "react";

export type ViewportBreakpoint = "mobile" | "tablet" | "desktop";

function getBreakpoint(width: number): ViewportBreakpoint {
  if (width < 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
}

export function useViewportBreakpoint(): ViewportBreakpoint {
  const [breakpoint, setBreakpoint] = useState<ViewportBreakpoint>(() =>
    typeof window !== "undefined"
      ? getBreakpoint(window.innerWidth)
      : "desktop"
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleResize() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setBreakpoint(getBreakpoint(window.innerWidth));
      }, 150);
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return breakpoint;
}
